// src/Components/GoogleMap/PolygonManager.js

import { useState, useRef, useEffect, useCallback } from 'react';
import { doc, deleteField, writeBatch, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../Firebase/firebase';
import { usePolygonDraw } from './PolygonDraw';

const sanitizeForFirestore = (data) => {
  const clean = { ...data };
  Object.keys(clean).forEach((key) => {
    if (clean[key] === undefined) clean[key] = null;
  });
  return clean;
};

const deleteStorageImage = async (imageUrl) => {
  if (!imageUrl) return;
  try {
    if (imageUrl.includes('firebasestorage')) {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    }
  } catch (error) {
    if (error.code !== 'storage/object-not-found') {
      console.warn('[PolygonManager] Error deleting file from storage:', error);
    }
  }
};

const ROGUE_FIELDS = [
  'groupHasMarker',
  'groupHasPolygon',
  'groupHasPolyline',
  'initialStyleIcon',
  'icon',
  'iconID',
  'isSaved',
  'type',
  'id',
  'isDrawing',
  'isDrawingLine',
  'isPlacingMarker',
  'path',
  'paths',
  'position',
  'color',
  'weight',
  'radius',
  'geometry',
  'markerGroupID',
  'isSilentAutoSave',
  '_screenX',
  '_screenY',
  '_shiftIndex',
  '_shiftedPos',
];

const getCoord = (pt, type) => {
  if (pt == null) return NaN;
  if (type === 'lat') {
    if (typeof pt.lat === 'function') return pt.lat();
    if (pt.lat !== undefined) return pt.lat;
    if (pt.latitude !== undefined) return pt.latitude;
    if (Array.isArray(pt)) return pt[0];
  } else {
    if (typeof pt.lng === 'function') return pt.lng();
    if (pt.lng !== undefined) return pt.lng;
    if (pt.longitude !== undefined) return pt.longitude;
    if (Array.isArray(pt)) return pt[1];
  }
  return NaN;
};

export const usePolygonManager = (
  mapInstance,
  polygonsData,
  setPolygonsData,
  polygonSelectionMode,
  setTooltip,
  onDebugUpdate,
  setSelectedMarkerData,
  globalLockRef,
  setIsSaving,
  activeMenu,
  clearAllExcept,
  setInteractionLock,
  isDrawMode,
  interactionLockRef,
  activeTool,
  drawingStyles,
) => {
  const [isEditingPolygon, setIsEditingPolygon] = useState(false);
  const [activePolygonId, setActivePolygonId] = useState(null);
  const activePolygonIdRef = useRef(null);

  const polygonRefs = useRef({});
  const selectionModeRef = useRef(polygonSelectionMode);
  const activeMenuRef = useRef(activeMenu);
  const editListenersRef = useRef([]);

  const polygonsDataRef = useRef(polygonsData);
  const originalStateRef = useRef({});

  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const updateLockTimeoutRef = useRef(null);

  const clearAllExceptRef = useRef(clearAllExcept);
  useEffect(() => {
    clearAllExceptRef.current = clearAllExcept;
  }, [clearAllExcept]);

  const setTooltipRef = useRef(setTooltip);
  useEffect(() => {
    setTooltipRef.current = setTooltip;
  }, [setTooltip]);

  const changePolygonColorRef = useRef(null);

  const isClickableRef = useRef(true);

  const isPermittedUserRef = useRef(false);
  const restrictedSettingsRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSettings = async () => {
      try {
        const restRef = doc(db, 'settings', 'RestrictedMarkerSettings');
        const restSnap = await getDoc(restRef);
        if (isMounted && restSnap.exists()) {
          restrictedSettingsRef.current = restSnap.data();
        }
      } catch (e) {
        console.error('Error fetching restricted settings', e);
      }
    };
    fetchSettings();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      if (user && user.email) {
        try {
          const emailKey = user.email.toLowerCase().trim();
          const permRef = doc(db, 'permittedUsers', emailKey);
          const permSnap = await getDoc(permRef);
          if (isMounted) {
            isPermittedUserRef.current = permSnap.exists();
          }
        } catch (e) {
          console.error('Error fetching permission', e);
        }
      } else {
        if (isMounted) {
          isPermittedUserRef.current = false;
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const setPolygonsClickable = useCallback((clickable) => {
    isClickableRef.current = clickable;
    Object.values(polygonRefs.current).forEach((poly) => {
      if (poly) {
        poly.setOptions({ clickable: clickable });
      }
    });
  }, []);

  useEffect(() => {
    setPolygonsClickable(isClickableRef.current);
  }, [isDrawMode, setPolygonsClickable]);

  useEffect(() => {
    polygonsDataRef.current = polygonsData;
    window.__polygonArray = polygonsData;
  }, [polygonsData]);
  useEffect(() => {
    activePolygonIdRef.current = activePolygonId;
  }, [activePolygonId]);
  useEffect(() => {
    selectionModeRef.current = polygonSelectionMode;
  }, [polygonSelectionMode]);
  useEffect(() => {
    activeMenuRef.current = activeMenu;
  }, [activeMenu]);

  const setSelectedMarkerDataRef = useRef(setSelectedMarkerData);
  useEffect(() => {
    setSelectedMarkerDataRef.current = setSelectedMarkerData;
  }, [setSelectedMarkerData]);

  const enablePolyEditingRef = useRef(null);

  const enablePolyEditing = useCallback(
    (id, caller = 'Unknown') => {
      if (clearAllExceptRef.current) {
        const polyData = polygonsDataRef.current.find((p) => p.id === id);
        clearAllExceptRef.current('POLYGON', polyData);
      }

      if (activePolygonIdRef.current !== id) {
        const polyData = polygonsDataRef.current.find((p) => p.id === id);
        if (polyData) {
          originalStateRef.current[id] = JSON.parse(JSON.stringify(polyData));
        }
      }

      const poly = polygonRefs.current[id];
      if (!poly || !window.google) return;

      const freshData =
        polygonsDataRef.current.find((p) => p.id === id) || poly.metadata || {};

      activePolygonIdRef.current = id;
      setActivePolygonId(id);
      setIsEditingPolygon(true);

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current({ ...freshData, id, type: 'polygon' });
      }

      if (
        activeToolRef.current === 'POLYGON' ||
        activeMenuRef.current === 'MARKUP_MARKERS'
      ) {
        poly.setEditable(true);
        poly.setDraggable(false);
      } else {
        poly.setEditable(false);
        poly.setDraggable(false);
      }

      if (editListenersRef.current.length > 0) {
        editListenersRef.current.forEach((l) =>
          window.google.maps.event.removeListener(l),
        );
        editListenersRef.current = [];
      }

      const path = poly.getPath();

      const onUpdate = () => {
        if (setInteractionLock) setInteractionLock(true);
        window.__vertexInteractionLock = true;

        if (updateLockTimeoutRef.current) clearTimeout(updateLockTimeoutRef.current);
        updateLockTimeoutRef.current = setTimeout(() => {
          if (setInteractionLock) setInteractionLock(false);
          window.__vertexInteractionLock = false;
        }, 500);

        const newPaths = path.getArray().map((p) => p.toJSON());
        if (setSelectedMarkerDataRef.current) {
          setSelectedMarkerDataRef.current((prev) => ({ ...prev, paths: newPaths }));
        }

        setPolygonsData((prev) =>
          prev.map((p) => (p.id === id ? { ...p, paths: newPaths } : p)),
        );
      };

      editListenersRef.current.push(
        poly.addListener('dragstart', () => {
          if (setInteractionLock) setInteractionLock(true);
          window.__vertexInteractionLock = true;
        }),
      );

      editListenersRef.current.push(
        path.addListener('set_at', (idx) => {
          const pt = path.getAt(idx);
          if (onDebugUpdate)
            onDebugUpdate({
              stage: 'VERTEX MOVED',
              points: path.getLength(),
              coords: `${pt.lat().toFixed(5)}, ${pt.lng().toFixed(5)}`,
            });
          onUpdate();
        }),
      );

      editListenersRef.current.push(
        path.addListener('insert_at', (idx) => {
          const pt = path.getAt(idx);
          if (onDebugUpdate)
            onDebugUpdate({
              stage: 'VERTEX ADDED',
              points: path.getLength(),
              coords: `${pt.lat().toFixed(5)}, ${pt.lng().toFixed(5)}`,
            });
          onUpdate();
        }),
      );

      editListenersRef.current.push(
        path.addListener('remove_at', (idx) => {
          if (onDebugUpdate)
            onDebugUpdate({ stage: 'VERTEX REMOVED', points: path.getLength() });
          onUpdate();
        }),
      );

      editListenersRef.current.push(poly.addListener('dragend', onUpdate));
    },
    [setInteractionLock, onDebugUpdate, setPolygonsData],
  );

  useEffect(() => {
    enablePolyEditingRef.current = enablePolyEditing;
  }, [enablePolyEditing]);

  useEffect(() => {
    const id = activePolygonId;
    if (id && polygonRefs.current[id]) {
      const poly = polygonRefs.current[id];
      const isTargetTool = activeTool === 'POLYGON';
      poly.setEditable(isTargetTool || activeMenuRef.current === 'MARKUP_MARKERS');
      poly.setDraggable(false);
    }
  }, [activeTool, activePolygonId]);

  const {
    isDrawing,
    isDrawingRef,
    startDrawing,
    cancelDrawing,
    handleDrawClick,
    handleMouseMove,
    completePolygon,
  } = usePolygonDraw(
    mapInstance,
    setPolygonsData,
    setSelectedMarkerData,
    onDebugUpdate,
    setActivePolygonId,
    (id) =>
      enablePolyEditingRef.current &&
      enablePolyEditingRef.current(id, 'usePolygonDraw finish'),
    drawingStyles,
  );

  useEffect(() => {
    if (polygonSelectionMode && activePolygonIdRef.current) {
      enablePolyEditing(activePolygonIdRef.current, 'polygonSelectionMode Effect');
    }
  }, [polygonSelectionMode, enablePolyEditing]);

  const changePolygonColor = useCallback(
    (color) => {
      const id = activePolygonIdRef.current;
      if (!id || !color) return;

      const poly = polygonRefs.current[id];
      if (!poly) {
        return;
      }

      const currentColor = poly.metadata?.color || '#FF9900';

      if (currentColor.toLowerCase() === color.toLowerCase()) {
        return;
      }

      poly.setOptions({
        strokeColor: color,
        fillColor: color,
      });

      poly.metadata = { ...poly.metadata, color: color };

      setPolygonsData((prev) => {
        const next = prev.map((p) => (p.id === id ? { ...p, color: color } : p));
        polygonsDataRef.current = next;
        return next;
      });

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current((prev) =>
          prev && prev.id === id ? { ...prev, color: color } : prev,
        );
      }
    },
    [setPolygonsData],
  );

  useEffect(() => {
    changePolygonColorRef.current = changePolygonColor;
  }, [changePolygonColor]);

  const loadPolygons = useCallback(
    (google) => {
      if (!mapInstance.current) return;

      const today = new Date().toISOString().split('T')[0];
      const rSettings = restrictedSettingsRef.current;
      const isPermitted = isPermittedUserRef.current;

      const visiblePolygons = [];
      polygonsData.forEach((p) => {
        let displayData = { ...p };

        if (!isPermitted) {
          let vis = p.visibility;
          if (!vis) {
            if (p.publicFromDate && p.publicFromDate.trim() !== '')
              vis = `Public From ${p.publicFromDate}`;
            else if (p.isPrivate) vis = 'Private';
            else vis = 'Public';
          }

          if (vis === 'Private') return;
          if (vis && vis.startsWith('Public From')) {
            const datePart = vis.replace('Public From', '').trim();
            if (datePart && datePart > today) return;
          }

          if (vis === 'Restricted' && rSettings) {
            displayData.hoverImage = rSettings.RestrictedHoverImage || '';
            displayData.hoverContent = rSettings.RestrictedHoverText || '';
          }
        }
        visiblePolygons.push(displayData);
      });

      const currentIds = new Set(visiblePolygons.map((p) => p.id));
      Object.keys(polygonRefs.current).forEach((id) => {
        if (!currentIds.has(id) && id !== activePolygonIdRef.current) {
          const poly = polygonRefs.current[id];
          if (poly) {
            poly.setEditable(false);
            poly.setDraggable(false);
            poly.setOptions({ clickable: false });
            poly.setMap(null);
          }
          delete polygonRefs.current[id];
        }
      });

      visiblePolygons.forEach((polyData) => {
        if (!polyData.paths?.length) return;

        let cleanPaths = [];
        try {
          const isMulti = Array.isArray(polyData.paths[0]);
          if (isMulti) {
            cleanPaths = polyData.paths.map((ring) =>
              ring
                .map((pt) => ({
                  lat: parseFloat(getCoord(pt, 'lat')),
                  lng: parseFloat(getCoord(pt, 'lng')),
                }))
                .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng)),
            );
          } else {
            cleanPaths = polyData.paths
              .map((pt) => ({
                lat: parseFloat(getCoord(pt, 'lat')),
                lng: parseFloat(getCoord(pt, 'lng')),
              }))
              .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng));
          }
        } catch (e) {
          console.error('Invalid polygon paths:', polyData.paths);
          return;
        }

        let polygon = polygonRefs.current[polyData.id];
        const shouldBeClickable = isClickableRef.current;

        if (!polygon) {
          try {
            polygon = new google.maps.Polygon({
              paths: cleanPaths,
              strokeColor: polyData.color || '#FF9900',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: polyData.color || '#FF9900',
              fillOpacity: 0.35,
              map: mapInstance.current,
              editable: false,
              draggable: false,
              zIndex: 1,
              clickable: shouldBeClickable,
            });
            polygonRefs.current[polyData.id] = polygon;

            polygon.addListener('click', (e) => {
              if (e?.stop) e.stop();
              if (e?.domEvent) {
                e.domEvent.stopPropagation();
                e.domEvent.preventDefault();
              }

              window.__shapeClickLock = true;
              setTimeout(() => {
                window.__shapeClickLock = false;
              }, 300);

              if (isDrawingRef.current) return;

              const isActiveCur = polyData.id === activePolygonIdRef.current;
              if (!isClickableRef.current && !isActiveCur) return;

              if (
                !selectionModeRef.current &&
                ![
                  'MARKUP_MARKERS',
                  'HOME',
                  'VIEW_INFO',
                  'UPDATE_INFO',
                  'MARKUP_INFO',
                ].includes(activeMenuRef.current)
              ) {
                return;
              }

              const currentId = activePolygonIdRef.current;
              const newId = polyData.id;

              if (currentId === newId && activeToolRef.current === 'POLYGON') return;

              const freshData = polygon.metadata || polyData;

              const canProceed = clearAllExceptRef.current
                ? clearAllExceptRef.current('POLYGON', { ...freshData, type: 'polygon' })
                : true;
              if (!canProceed) return;

              if (currentId && currentId !== newId) {
                const oldPoly = polygonRefs.current[currentId];
                if (oldPoly) {
                  oldPoly.setEditable(false);
                  oldPoly.setDraggable(false);
                }
              }

              if (changePolygonColorRef.current && freshData.color) {
                changePolygonColorRef.current(freshData.color);
              }

              if (enablePolyEditingRef.current)
                enablePolyEditingRef.current(freshData.id, 'Polygon Click Listener');
            });

            polygon.addListener('mousemove', (e) => {
              if (isDrawingRef.current) return;

              const isActiveCur = polyData.id === activePolygonIdRef.current;
              if (!isClickableRef.current && !isActiveCur) return;

              const data = polygon.metadata || {};
              if (setTooltipRef.current) {
                setTooltipRef.current((prev) => {
                  if (prev && prev.source === 'MARKER') return prev;
                  return {
                    source: 'SHAPE',
                    x: e.domEvent.clientX,
                    y: e.domEvent.clientY,
                    content: {
                      id: data.id,
                      image: data.hoverImage,
                      body: data.hoverContent,
                    },
                  };
                });
              }
            });

            polygon.addListener('mouseout', () => {
              if (setTooltipRef.current) {
                setTooltipRef.current((prev) =>
                  prev?.source === 'MARKER' ? prev : null,
                );
              }
            });
          } catch (err) {
            console.error('Failed to create Polygon geometry:', err);
          }
        } else {
          try {
            const isActive = polyData.id === activePolygonIdRef.current;
            if (!polygon.getMap()) polygon.setMap(mapInstance.current);
            if (!isActive) polygon.setPaths(cleanPaths);

            const oldMetadata = polygon.metadata || {};
            const oldColor = oldMetadata.color || '#FF9900';
            const newColor = polyData.color || '#FF9900';
            const oldClickable = polygon.get('clickable');

            if (oldColor !== newColor || oldClickable !== shouldBeClickable) {
              polygon.setOptions({
                clickable: shouldBeClickable,
                strokeColor: newColor,
                fillColor: newColor,
              });
            }
          } catch (err) {
            console.error('Failed to update existing Polygon geometry:', err);
          }
        }

        if (polygon) polygon.metadata = polyData;
      });
    },
    [polygonsData, mapInstance, isDrawingRef],
  );

  const saveEdits = useCallback(
    async (groupDataPayload) => {
      let newPaths;
      let id = groupDataPayload?.id || activePolygonIdRef.current;
      let isCompletingDraw = false;

      if (isDrawingRef.current) {
        const drawData = completePolygon(window.google, true);
        if (!drawData) return;
        newPaths = drawData.paths;
        id = drawData.id;
        isCompletingDraw = true;
      } else {
        if (!id) return;
        const poly = polygonRefs.current[id];
        if (!poly) return;
        if (editListenersRef.current.length > 0) {
          editListenersRef.current.forEach((l) =>
            window.google.maps.event.removeListener(l),
          );
          editListenersRef.current = [];
        }
        newPaths = poly
          .getPath()
          .getArray()
          .map((p) => p.toJSON());
      }

      if (setIsSaving) setIsSaving(true);
      try {
        const cleanGroupData = sanitizeForFirestore({ ...groupDataPayload });
        const groupId = cleanGroupData.markerGroupID || id;

        if (!groupDataPayload.isSilentAutoSave) {
          const firestoreGroupData = { ...cleanGroupData };
          ROGUE_FIELDS.forEach((field) => (firestoreGroupData[field] = deleteField()));

          delete firestoreGroupData.markerID;
          delete firestoreGroupData.polylineID;
          firestoreGroupData.polygonID = id;

          const batch = writeBatch(db);
          batch.set(doc(db, 'markerGroups', groupId), firestoreGroupData, {
            merge: true,
          });

          const memoryPoly = polygonsDataRef.current.find((p) => p.id === id) || {};
          const finalColor = memoryPoly.color || cleanGroupData.color || '#FF9900';

          const geometryData = {
            paths: newPaths,
            color: finalColor,
            markerGroupID: groupId,
          };

          batch.set(doc(db, 'polygons', id), geometryData, { merge: true });
          await batch.commit();

          ROGUE_FIELDS.forEach((field) => delete cleanGroupData[field]);
          const dataWithId = {
            ...cleanGroupData,
            ...geometryData,
            id: id,
            type: 'polygon',
            isNew: false,
            isSaved: true,
          };
          if (
            cleanGroupData.publicFromDate &&
            typeof cleanGroupData.publicFromDate === 'object'
          )
            delete dataWithId.publicFromDate;
          delete dataWithId.isSilentAutoSave;

          originalStateRef.current[id] = JSON.parse(JSON.stringify(dataWithId));

          setPolygonsData((prev) => {
            const next = prev.find((p) => p.id === id)
              ? prev.map((p) => (p.id === id ? dataWithId : p))
              : [...prev, dataWithId];
            polygonsDataRef.current = next;
            return next;
          });

          if (isCompletingDraw) {
            activePolygonIdRef.current = id;
            setActivePolygonId(id);
            if (setSelectedMarkerDataRef.current)
              setSelectedMarkerDataRef.current(dataWithId);
            setTimeout(() => {
              if (enablePolyEditingRef.current)
                enablePolyEditingRef.current(id, 'Polygon Save Edits');
              const visualPoly = polygonRefs.current[id];
              if (visualPoly)
                visualPoly.metadata = {
                  ...visualPoly.metadata,
                  ...dataWithId,
                  isNew: false,
                  isSaved: true,
                };
            }, 100);
          } else {
            const poly = polygonRefs.current[id];
            if (poly)
              poly.metadata = {
                ...poly.metadata,
                ...dataWithId,
                isNew: false,
                isSaved: true,
              };
            if (enablePolyEditingRef.current)
              enablePolyEditingRef.current(id, 'Polygon Save Edits');
            if (setSelectedMarkerDataRef.current)
              setSelectedMarkerDataRef.current(dataWithId);
          }
        } else {
          const batch = writeBatch(db);
          batch.set(doc(db, 'markerGroups', groupId), { polygonID: id }, { merge: true });

          const memoryPoly = polygonsDataRef.current.find((p) => p.id === id) || {};
          const geometryData = {
            paths: newPaths,
            color: memoryPoly.color || '#FF9900',
            markerGroupID: groupId,
          };
          batch.set(doc(db, 'polygons', id), geometryData, { merge: true });
          await batch.commit();

          const updatedDraft = {
            ...memoryPoly,
            ...geometryData,
            id,
            type: 'polygon',
            isNew: false,
            isSaved: true,
          };
          originalStateRef.current[id] = JSON.parse(JSON.stringify(updatedDraft));

          const poly = polygonRefs.current[id];
          if (poly) {
            poly.metadata = {
              ...poly.metadata,
              ...updatedDraft,
            };
          }

          setPolygonsData((prev) => {
            const next = prev.find((p) => p.id === id)
              ? prev.map((p) => (p.id === id ? updatedDraft : p))
              : [...prev, updatedDraft];
            polygonsDataRef.current = next;
            return next;
          });
        }
      } catch (error) {
        console.error('Error updating polygon:', error);
      } finally {
        if (setIsSaving) setIsSaving(false);
      }
    },
    [setPolygonsData, setIsSaving, completePolygon, isDrawingRef],
  );

  const deletePolygon = useCallback(
    async (idToDelete) => {
      if (setTooltipRef.current) {
        setTooltipRef.current(null);
        setTimeout(() => setTooltipRef.current(null), 50);
      }
      const id = idToDelete?.id || idToDelete || activePolygonIdRef.current;
      if (!id) return;
      if (setIsSaving) setIsSaving(true);

      const poly = polygonRefs.current[id];
      if (poly) {
        poly.setEditable(false);
        poly.setDraggable(false);
        poly.setOptions({ clickable: false });
        if (window.google) window.google.maps.event.clearInstanceListeners(poly);
        poly.setMap(null);
        delete polygonRefs.current[id];
      }

      try {
        const existingData = polygonsDataRef.current.find((p) => p.id === id);
        let groupId = existingData?.markerGroupID;

        if (!groupId) {
          const snap = await getDoc(doc(db, 'polygons', id));
          if (snap.exists() && snap.data().markerGroupID) {
            groupId = snap.data().markerGroupID;
          }
        }
        groupId = groupId || id;

        const markers = window.__markerArray || [];
        const polylines = window.__polylineArray || [];
        const siblingMarker = markers.find(
          (m) => String(m.markerGroupID || m.id) === String(groupId),
        );
        const siblingPolyline = polylines.find(
          (l) => String(l.markerGroupID || l.id) === String(groupId),
        );

        const batch = writeBatch(db);
        batch.delete(doc(db, 'polygons', id));

        if (!siblingMarker && !siblingPolyline) {
          batch.delete(doc(db, 'markerGroups', groupId));

          let hoverImageUrl = existingData?.hoverImage;
          let photosArray = existingData?.photos || [];
          let docsArray = existingData?.documents || [];

          if (!hoverImageUrl || photosArray.length === 0 || docsArray.length === 0) {
            const groupSnap = await getDoc(doc(db, 'markerGroups', groupId));
            if (groupSnap.exists()) {
              const groupData = groupSnap.data();
              hoverImageUrl = hoverImageUrl || groupData.hoverImage;
              if (photosArray.length === 0 && groupData.photos)
                photosArray = groupData.photos;
              if (docsArray.length === 0 && groupData.documents)
                docsArray = groupData.documents;
            }
          }

          if (hoverImageUrl) await deleteStorageImage(hoverImageUrl);

          if (photosArray && photosArray.length > 0) {
            for (const photo of photosArray) {
              if (photo.url) await deleteStorageImage(photo.url);
            }
          }

          if (docsArray && docsArray.length > 0) {
            for (const docItem of docsArray) {
              if (docItem.url) await deleteStorageImage(docItem.url);
            }
          }
        } else {
          batch.set(
            doc(db, 'markerGroups', groupId),
            { polygonID: deleteField() },
            { merge: true },
          );
        }

        await batch.commit();

        setPolygonsData((prev) => {
          const next = prev.filter((p) => p.id !== id);
          polygonsDataRef.current = next;
          return next;
        });

        delete originalStateRef.current[id];

        setIsEditingPolygon(false);
        activePolygonIdRef.current = null;
        setActivePolygonId(null);
        if (setSelectedMarkerDataRef.current) setSelectedMarkerDataRef.current(null);
      } catch (error) {
        console.error('Error deleting polygon:', error);
      } finally {
        if (setIsSaving) setIsSaving(false);
      }
    },
    [setPolygonsData, setIsSaving],
  );

  const cancelEdit = useCallback(
    (reason = 'Unknown', clearGlobal = true) => {
      if (setTooltipRef.current) {
        setTooltipRef.current(null);
        setTimeout(() => setTooltipRef.current(null), 50);
      }
      if (editListenersRef.current.length > 0) {
        editListenersRef.current.forEach((l) =>
          window.google.maps.event.removeListener(l),
        );
        editListenersRef.current = [];
      }
      const id = activePolygonIdRef.current;

      if (id) {
        const poly = polygonRefs.current[id];
        if (poly) {
          const original =
            originalStateRef.current[id] ||
            polygonsDataRef.current.find((p) => p.id === id);

          if (!original) {
            if (poly.metadata?.isSaved) {
              poly.setEditable(false);
              poly.setDraggable(false);
              poly.setOptions({ clickable: isClickableRef.current });
            } else {
              poly.setEditable(false);
              poly.setDraggable(false);
              poly.setOptions({ clickable: false });
              if (window.google) window.google.maps.event.clearInstanceListeners(poly);
              poly.setMap(null);
              delete polygonRefs.current[id];
            }
          } else {
            poly.setEditable(false);
            poly.setDraggable(false);
            poly.setOptions({
              clickable: isClickableRef.current,
              fillColor: original.color || '#FF9900',
              strokeColor: original.color || '#FF9900',
            });
            if (original.paths) poly.setPaths(original.paths);

            setPolygonsData((prev) => {
              const next = prev.map((p) => (p.id === id ? original : p));
              polygonsDataRef.current = next;
              return next;
            });
          }
        }
        delete originalStateRef.current[id];
      }
      setIsEditingPolygon(false);
      activePolygonIdRef.current = null;
      setActivePolygonId(null);
      if (clearGlobal && setSelectedMarkerDataRef.current)
        setSelectedMarkerDataRef.current(null);
    },
    [setPolygonsData],
  );

  const clearActivePolygon = useCallback(
    (forceDeselect = false, clearGlobal = true) => {
      if (activePolygonIdRef.current)
        cancelEdit('clearActivePolygon requested', clearGlobal);
    },
    [cancelEdit],
  );

  useEffect(() => {
    return () => {
      if (updateLockTimeoutRef.current) clearTimeout(updateLockTimeoutRef.current);
    };
  }, []);

  return {
    isEditingPolygon,
    activePolygonId,
    isDrawing,
    isDrawingRef,
    loadPolygons,
    saveEdits,
    deletePolygon,
    cancelEdit,
    changePolygonColor,
    clearActivePolygon,
    startDrawing,
    cancelDrawing,
    handleDrawClick,
    handleMouseMove,
    completePolygon,
    enablePolyEditing,
    activePolygonIdRef,
    setActivePolygonId,
    setPolygonsClickable,
  };
};
